import { HttpClient, HttpResponse } from "@angular/common/http";
import { BaseService } from "../services/_base.service";
import { UserService } from "../services/user.service";
import { first, map, Observable, Subject } from "rxjs";
import { inject, Injectable } from "@angular/core";
import { IndexedDBService } from "../services/indexed-db.service";
import moment from "moment";
import { PersistentHttpService } from "../services/persistent-http.service";

@Injectable()
export class ChatService extends BaseService{
  
  newComments$ = new Subject<any>();
  
  openHistory$ = new Subject<any>();

  reloadChats$ = new Subject<void>();
  
  private initialHeight: number | null = null;
  
  private readonly THRESHOLD = 100; // Threshold in pixels to detect keyboard open
  
  keyboardOpenSubject = new Subject<boolean>();
  
  public keyboardOpen$ = this.keyboardOpenSubject.asObservable();

  indexDb = inject(IndexedDBService);
  persistentHttp = inject(PersistentHttpService);
  user = inject(UserService);


  constructor() {
    super();
    if ('visualViewport' in window) {
      window.visualViewport.addEventListener('resize', () => {
        if (this.initialHeight === null) {
          this.initialHeight = window.visualViewport.height;
        }
        const keyboardIsOpen = window.visualViewport.height < (this.initialHeight - this.THRESHOLD);
        this.keyboardOpenSubject.next(keyboardIsOpen);
      });
    }
  }
  index() {
    return this.persistentHttp.index(`${this.apiURL}/chats`, 'chats',null, (chat) => {
      return chat.order?.ended_at == null && chat.order?.deleted_at == null;
    }, false);
  }
  get(id: number | string, thread_id?: number) {
    return new Observable<any>((observer) => {
      const url = `${this.apiURL}/chats/${id}`;
      this.persistentHttp.getLastSync(url).subscribe((sync) => {
        this.http.get(`${this.apiURL}/chats/${id}`, this.getParams({
          start_at: sync?.last_sync_id,
          thread_id: thread_id
        }))
          .pipe(
            first(),
            map((response: HttpResponse<any>) => response.body),
          )
          .subscribe({
            next: (chat) => {
              const lastMessage = chat.comments[chat.comments.length - 1];
              if (lastMessage) {
                this.persistentHttp.setLastSync(url, lastMessage.created_at, lastMessage.id)
              }
              observer.next(chat);
              observer.complete();
            },
            error: (error) => {
              console.error('Error fetching chat', error);
              observer.complete();
            }
          })
      });
    });
  }
  comment(id: number, message) {
    return new Observable<any>((observer) => {
      this.persistentHttp.create(`${this.apiURL}/chats`, 'messages', {
        ...message
      }, {
        ownership_id: id
      }, {
        chat_id: id,
      }).pipe(
        first()
      ).subscribe({
        next: (response) => {
          observer.next(response);
          observer.complete();
        },
        error: (error) => {
          console.error('Error posting comment', error);
          observer.error(error);
        }
      });
        
    })
  }
  updateComment(ownership_id, id: number, message) {
    return this.persistentHttp.update(`${this.apiURL}/chats/${id}`, 'messages', {
      ...message
    }, {
      ownership_id: ownership_id
    }, {
      chat_id: id,
    })
  }
  deleteComment(id: number) {
    return this.persistentHttp.softDestroy(`${this.apiURL}/chats/${id}`, id, 'messages', {
      ownership_id: id
    },{
      comment: null,
      pictures: [],
      calendar_event: null,
      reply: null,
      deleted_at: moment().toISOString(),
    })
  }

  readChat(id,last_id){
    return this.persistentHttp.update(`${this.apiURL}/chats/${id}/read`, 'chats',{
      id: id
    }, this.getParams(),{
      last_sync: last_id,
    })
  }


  addChat(type: string, id: number, switchToChatView = true) {
    return new Observable<any>((observer) => {
      this.http.post(`${this.apiURL}/chats/${id}/add_owner`, {
        type: type,
        id: id
      }, this.getParams())
        .pipe(
          map((response: HttpResponse<any>) => response.body),
          first()
        ).subscribe({
          next: (response) => {
            this.writeAndOpenChat(observer, response, switchToChatView);
          },
          error: (error) => {
            console.error('Error adding chat:', error);
            observer.error(error);
          }
        }
        );
    });
  }

  writeAndOpenChat(observer, chat, switchToChatView = true) {
    const write = this.indexDb.write('chats', chat);
    if (write) {
      write.onsuccess = () => {
        if(observer){
          this.openChat(
            chat.document_type,
            chat.document_id,
            chat.id,
            switchToChatView,
          );
          observer.next(chat);
          observer.complete();
        }
      }
      write.onerror = () => {
        console.error('Error writing chat to IndexedDB');
        observer.error('Error writing chat to IndexedDB');
      }
    }
  }

  openChat(document_type, document_id, chat_id?, switchToChatView = true) {
    this.openHistory$.next({
      document_type,
      document_id,
      chat_id,
      switchToChatView,
    });
  }

  getAgentHistory(){
    return this.http.get(`${this.apiURL}/chats/agent_history`, this.getParams())
      .pipe(
        map((response: HttpResponse<any>) => response.body),
      );
  }
} 